import React, { useCallback, useMemo, useEffect } from 'react'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBedSofaIcon from 'LuxKit/Icons/line/LineBedSofaIcon'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineSignAltIcon from 'LuxKit/Icons/line/LineSignAltIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

export const TRIP_PACES = [
  {
    tripPace: 'Relaxed',
    title: 'Relaxed',
    description: 'Take your time, enjoy the vibe.',
    icon: <LineBedSofaIcon />,
  },
  {
    tripPace: 'Balanced',
    title: 'Explore a little',
    description: 'A mix of activities and relaxation.',
    icon: <LineBalanceScaleIcon />,
  },
  {
    tripPace: 'Active',
    title: 'Active',
    description: 'Pack in as much as possible.',
    icon: <LineSignAltIcon />,
  },
]

interface Props {
  hotelCity: string
  tripPaces: Array<{
    tripPace: string
    selected: boolean
  }>
  onPaceSelect: (pace: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripPaceSelection({
  hotelCity,
  tripPaces,
  onPaceSelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const handleOnClick = useCallback((pace: string) => {
    onPaceSelect(pace)
    onNext()
  }, [onPaceSelect, onNext])

  const tripPaceMap = useMemo(() =>
    arrayToMap(tripPaces, pace => pace.tripPace, pace => pace.selected),
  [tripPaces])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'trip_pace',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepNumber} of {totalSteps}</BodyText>
        <Heading variant="heading1">
          Trip <i>Pace</i>
        </Heading>
        <BodyText variant="large">
          What kind of tempo are you after for your {hotelCity} trip?
        </BodyText>
      </VerticalSpacer>

      <TileCardsGroup>
        {TRIP_PACES.map(pace => (
          <TileCard
            key={pace.tripPace}
            title={pace.title}
            subtitle={pace.description}
            icon={pace.icon}
            value={pace.tripPace}
            onClick={handleOnClick}
            selected={!!tripPaceMap.get(pace.tripPace)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripPaceSelection
